import { api } from "@convex/_generated/api"; // Use alias
import type { Id } from "@convex/_generated/dataModel"; // Id importieren
import { useMutation } from "convex/react";
import { FileUp, UploadCloud, X } from "lucide-react";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress"; // Use Progress component
import { formatBytes } from "@/lib/utils";

interface FileUploaderProps {
	raumId: string;
	// nutzerHash: string; // nutzerHash wird nicht mehr benötigt, da Clerk verwendet wird
	maxSize: number; // Remaining space in bytes
	onSuccess: (message: string) => void;
	onError: (message: string) => void;
}

interface UploadingFile {
	id: string;
	file: File;
	progress: number;
	status: "uploading" | "success" | "error";
	errorMessage?: string;
}

export function FileUploader({
	raumId,
	// nutzerHash, // nutzerHash wird nicht mehr benötigt
	maxSize,
	onSuccess,
	onError,
}: FileUploaderProps) {
	const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
	const generateUploadUrl = useMutation(api.fileShare.generateUploadUrl);
	const dateiHochladenMutation = useMutation(api.fileShare.dateiHochladen);

	const updateFileProgress = useCallback((id: string, progress: number) => {
		setUploadingFiles((prev) =>
			prev.map((f) => (f.id === id ? { ...f, progress } : f)),
		);
	}, []);

	const updateFileStatus = useCallback(
		(id: string, status: "success" | "error", errorMessage?: string) => {
			setUploadingFiles((prev) =>
				prev.map((f) =>
					f.id === id ? { ...f, status, progress: 100, errorMessage } : f,
				),
			);
		},
		[],
	);

	const removeFile = useCallback((id: string) => {
		setUploadingFiles((prev) => prev.filter((f) => f.id !== id));
	}, []);

	const onDrop = useCallback(
		async (acceptedFiles: File[]) => {
			const currentUploadSize = uploadingFiles
				.filter((f) => f.status === "uploading")
				.reduce((acc, f) => acc + f.file.size, 0);

			let availableSpace = maxSize - currentUploadSize;
			const filesToUpload: File[] = [];

			for (const file of acceptedFiles) {
				if (availableSpace >= file.size) {
					filesToUpload.push(file);
					availableSpace -= file.size;
				} else {
					onError(
						`Nicht genügend Speicherplatz für "${file.name}". Überspringt.`,
					);
				}
			}

			if (filesToUpload.length === 0) {
				if (acceptedFiles.length > 0)
					onError("Keine Datei kann hochgeladen werden (Speicherlimit).");
				return;
			}

			const newUploads: UploadingFile[] = filesToUpload.map((file) => ({
				id: `${file.name}-${file.size}-${Date.now()}`, // Simple unique ID
				file,
				progress: 0,
				status: "uploading",
			}));

			setUploadingFiles((prev) => [...prev, ...newUploads]);

			for (const upload of newUploads) {
				try {
					// Step 1: Get a short-lived upload URL (room-based permission)
					const uploadUrl = await generateUploadUrl({ raumId });

					// Step 2: Upload the file with progress tracking
					const xhr = new XMLHttpRequest();
					xhr.upload.addEventListener("progress", (event) => {
						if (event.lengthComputable) {
							updateFileProgress(
								upload.id,
								Math.round((event.loaded / event.total) * 100),
							);
						}
					});

					// Create a Promise to handle the XHR request
					const uploadPromise = new Promise<string>((resolve, reject) => {
						xhr.open("POST", uploadUrl);
						xhr.setRequestHeader(
							"Content-Type",
							upload.file.type || "application/octet-stream",
						);

						xhr.onload = async () => {
							if (xhr.status >= 200 && xhr.status < 300) {
								const response = JSON.parse(xhr.responseText);
								resolve(response.storageId);
							} else {
								reject(new Error(`Upload failed with status ${xhr.status}`));
							}
						};

						xhr.onerror = () =>
							reject(new Error("Network error during upload"));
						xhr.send(upload.file);
					});

					// Wait for the upload to complete
					const storageId = (await uploadPromise) as Id<"_storage">; // Cast to Id<"_storage">

					// Call the mutation to save metadata
					await dateiHochladenMutation({
						raumId,
						storageId,
						name: upload.file.name,
						typ: upload.file.type || "application/octet-stream",
						groesse: upload.file.size,
						// nutzerHash, // nutzerHash wird nicht mehr an die Mutation übergeben
					});

					updateFileStatus(upload.id, "success");
					onSuccess(`"${upload.file.name}" erfolgreich hochgeladen.`);
					// Remove successful upload after a delay
					setTimeout(() => removeFile(upload.id), 3000);
				} catch (err: unknown) {
					console.error(`Upload failed for ${upload.file.name}:`, err);
					const message =
						(err as Error).message ||
						`Upload für "${upload.file.name}" fehlgeschlagen.`;
					updateFileStatus(upload.id, "error", message);
					onError(message);
					// Keep failed upload visible for a while
					setTimeout(() => removeFile(upload.id), 5000);
				}
			}
		},
		[
			generateUploadUrl,
			dateiHochladenMutation,
			raumId,
			maxSize,
			onError,
			onSuccess,
			uploadingFiles,
			removeFile,
			updateFileProgress,
			updateFileStatus,
		],
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		disabled: maxSize <= 0, // Disable dropzone if no space left
	});

	return (
		<section className="card rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/70">
			<h2 className="mb-4 border-gray-100 border-b pb-2 font-semibold text-gray-800 text-xl dark:border-gray-700 dark:text-gray-100">
				Dateien hochladen
			</h2>

			{maxSize <= 0 ? (
				<div className="rounded-lg border border-red-100 bg-red-50 p-6 text-center dark:border-red-700/50 dark:bg-red-900/30">
					<p className="font-medium text-red-700 dark:text-red-300">
						Speicherlimit erreicht!
					</p>
					<p className="text-red-600 text-sm dark:text-red-400">
						Du kannst keine weiteren Dateien hochladen, da der Speicherplatz für
						diesen Raum voll ist.
					</p>
				</div>
			) : (
				<div
					{...getRootProps()}
					className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors${
						isDragActive
							? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
							: "border-gray-300 hover:border-blue-400 hover:bg-gray-50 dark:border-gray-600 dark:hover:border-blue-500 dark:hover:bg-gray-700/50"
					}`}
				>
					<input {...getInputProps()} />
					<UploadCloud className="mx-auto mb-4 h-12 w-12 text-gray-400 dark:text-gray-500" />
					{isDragActive ? (
						<p className="font-semibold text-blue-600 dark:text-blue-400">
							Dateien hier ablegen...
						</p>
					) : (
						<>
							<p className="font-semibold text-gray-700 dark:text-gray-300">
								Dateien per Drag & Drop hier ablegen
							</p>
							<p className="mt-1 text-gray-500 text-sm dark:text-gray-400">
								oder klicken, um Dateien auszuwählen
							</p>
							<p className="mt-3 text-gray-400 text-xs dark:text-gray-500">
								Verbleibender Speicher: {formatBytes(maxSize)}
							</p>
						</>
					)}
				</div>
			)}

			{/* Upload Progress List */}
			{uploadingFiles.length > 0 && (
				<div className="mt-6 space-y-3">
					{uploadingFiles.map((upload) => (
						<div
							className={`rounded-lg border p-3 ${
								upload.status === "error"
									? "border-red-200 bg-red-50 dark:border-red-700 dark:bg-red-900/30"
									: "border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-700/50"
							}`}
							key={upload.id}
						>
							<div className="flex items-center justify-between gap-2">
								<div className="flex items-center gap-2 overflow-hidden">
									<FileUp className="h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400" />
									<span className="truncate font-medium text-gray-800 text-sm dark:text-gray-200">
										{upload.file.name}
									</span>
									<span className="shrink-0 text-gray-500 text-xs dark:text-gray-400">
										({formatBytes(upload.file.size)})
									</span>
								</div>
								<Button
									className="h-6 w-6 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
									onClick={() => removeFile(upload.id)}
									size="icon"
									variant="ghost"
								>
									<X className="h-4 w-4" />
								</Button>
							</div>
							{upload.status === "uploading" && (
								<Progress
									aria-label={`${upload.progress}% hochgeladen`}
									className="mt-2 h-2"
									value={upload.progress}
								/>
							)}
							{upload.status === "error" && (
								<p className="mt-1 text-red-600 text-xs dark:text-red-400">
									Fehler: {upload.errorMessage}
								</p>
							)}
						</div>
					))}
				</div>
			)}
		</section>
	);
}
